/**
 * The query keys the Pages section shares (design.md "Query keys", P24).
 *
 * Every key names the organization, because switching organization - a Super
 * Admin's switch, or staff viewing another - does not clear the cache: without
 * it one organization's pages would show under the other's name (the lesson
 * of `arcKey`). Every key sits under `pagesRoot`, so one invalidation after
 * any write refreshes the list, an open editor and the picker together
 * (design.md I7). Spelled once here: a screen that spelled its own would keep
 * showing what the write replaced.
 */
import type { PagesParams } from './usePagesParams';

/** Everything a page write can change, for one organization. */
export const pagesRoot = (organizationId: string | null) =>
  ['pages', organizationId] as const;

/** One page of the list, keyed by everything the request carries. */
export const pagesListKey = (organizationId: string | null, params: PagesParams) =>
  [...pagesRoot(organizationId), 'list', params] as const;

/** One page with its content, links and limits, for the editor and Preview. */
export const pageDetailKey = (organizationId: string | null, id: string) =>
  [...pagesRoot(organizationId), 'detail', id] as const;

/** One page of the document picker's results. */
export const pageDocumentsKey = (
  organizationId: string | null,
  q: string,
  page: number,
) => [...pagesRoot(organizationId), 'documents', q, page] as const;

/**
 * The limits a new page is drawn against, read from the list reply
 * (`GET /pages?limit=1`) under its own key, so it never shares the list's
 * cache entry.
 */
export const pageLimitsKey = (organizationId: string | null) =>
  [...pagesRoot(organizationId), 'limits'] as const;
